package com.fantasytenis.web.screens.contests

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.ContestType
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.contests.entity.PrizeLevel
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.lineup.entity.LineupEntity
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ContestStatusBadge
import com.fantasytenis.web.components.ContestTypeBadge
import com.fantasytenis.web.components.FeaturedBadge
import com.fantasytenis.web.components.GuaranteedBadge
import com.fantasytenis.web.components.PerfectLineupSection
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.components.StickyBottomCTA
import com.fantasytenis.web.components.StickyBottomCTASpacer
import com.fantasytenis.web.components.ScoringCategoryCard
import com.fantasytenis.web.components.getCategoryColor
import com.fantasytenis.web.components.getCategoryIcon
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.isMobile
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeColors
import com.fantasytenis.web.utils.ContestUtils
import kotlinx.browser.localStorage
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

/**
 * Screen displaying contest details with entry option.
 * Redesigned with focus on first-time user education and clear visual hierarchy.
 */
@Composable
fun ContestDetailScreen(contestId: String) {
    val colors = Theme.colors
    val scope = rememberCoroutineScope()
    val isMobileView = isMobile()

    // Existing state
    var contest by remember { mutableStateOf<ContestEntity?>(null) }
    var userEntries by remember { mutableStateOf<List<EntryEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var showPrizeStructure by remember { mutableStateOf(false) }

    // New state for lineup preview
    var entryLineups by remember { mutableStateOf<Map<String, LineupEntity>>(emptyMap()) }
    var slatePlayersMap by remember { mutableStateOf<Map<String, TennisPlayerEntity>>(emptyMap()) }

    // How to Play section state - collapsed on mobile by default, expanded on desktop
    // Persisted to localStorage for user preference
    var showHowToPlay by remember(isMobileView) {
        val stored = localStorage.getItem("howToPlayCollapsed")
        mutableStateOf(
            if (isMobileView) stored == "false"  // Collapsed by default on mobile
            else stored != "true"                 // Expanded by default on desktop
        )
    }

    // Load contest details
    LaunchedEffect(contestId) {
        isLoading = true
        error = null

        DependencyContainer.getContestUseCase(contestId).fold(
            onSuccess = { loadedContest ->
                contest = loadedContest
                // Auto-expand prize structure for free contests
                if (loadedContest.entryFee == 0.0) {
                    showPrizeStructure = true
                }
            },
            onFailure = { error = it.message }
        )

        // Load user's entries for this contest
        DependencyContainer.getUserContestEntriesUseCase(contestId = contestId).fold(
            onSuccess = { entries ->
                userEntries = entries

                // Load lineup data for each entry
                val lineupMap = mutableMapOf<String, LineupEntity>()
                entries.forEach { entry ->
                    entry.lineupId?.let { lineupId ->
                        DependencyContainer.getLineupUseCase(lineupId).fold(
                            onSuccess = { lineup -> lineupMap[entry.id] = lineup },
                            onFailure = { /* non-critical */ }
                        )
                    }
                }
                entryLineups = lineupMap
            },
            onFailure = { /* User might not have entries */ }
        )

        // Load players for lineup preview
        contest?.let { c ->
            DependencyContainer.getSlatePlayersUseCase(c.tournamentId, "M").fold(
                onSuccess = { players ->
                    slatePlayersMap = players.associateBy { it.id }
                },
                onFailure = { /* non-critical */ }
            )
        }

        isLoading = false
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = ContestUtils.getDisplayName(contest?.name) ?: "Contest Details",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.Contests) },
            customActions = {
                // Status badge and leaderboard button (visible on desktop)
                contest?.let { c ->
                    if (c.status == ContestStatus.LIVE || c.status == ContestStatus.COMPLETED) {
                        Button(
                            text = "Leaderboard",
                            onClick = { NavigationState.navigate(Route.ContestLeaderboard(contestId)) },
                            variant = "secondary"
                        )
                    }
                    ContestStatusBadge(c.status)
                }
            }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            Div({
                style {
                    padding(24.px)
                    maxWidth(900.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
                when {
                    isLoading -> LoadingState(colors)
                    error != null -> ErrorState(error!!, colors) {
                        scope.launch {
                            isLoading = true
                            DependencyContainer.getContestUseCase(contestId).fold(
                                onSuccess = { contest = it; isLoading = false },
                                onFailure = { error = it.message; isLoading = false }
                            )
                        }
                    }
                    contest != null -> {
                        val c = contest!!

                        // 1. Contest Header Card with Prize Pool Hero
                        ContestHeaderCard(c, colors)

                        // 2. How to Play Section (NEW - for first-time users)
                        HowToPlaySection(
                            salaryCap = c.salaryCap,
                            contestName = c.name,
                            isExpanded = showHowToPlay,
                            onToggle = {
                                showHowToPlay = !showHowToPlay
                                localStorage.setItem("howToPlayCollapsed", (!showHowToPlay).toString())
                            },
                            colors = colors
                        )

                        // 3. Unified Entry Management Section
                        EntryManagementSection(
                            contest = c,
                            userEntries = userEntries,
                            entryLineups = entryLineups,
                            slatePlayersMap = slatePlayersMap,
                            onEnter = { NavigationState.navigate(Route.Lineup(contestId)) },
                            onEditEntry = { entry ->
                                NavigationState.navigate(Route.Lineup(
                                    contestId = contestId,
                                    entryId = entry.id,
                                    lineupId = entry.lineupId
                                ))
                            },
                            colors = colors
                        )

                        // 4. Perfect Lineup Section (only for completed contests)
                        if (c.status == ContestStatus.COMPLETED && c.perfectLineup != null) {
                            // Get the user's best entry lineup for comparison
                            val userBestEntry = userEntries.maxByOrNull { it.totalPoints }
                            val userLineup = userBestEntry?.let { entryLineups[it.id] }

                            PerfectLineupSection(
                                perfectLineup = c.perfectLineup!!,
                                userLineup = userLineup,
                                playersMap = slatePlayersMap,
                                colors = colors
                            )
                        }

                        // 5. Prize Structure Section
                        PrizeStructureSection(
                            prizeStructure = c.prizeStructure,
                            totalPrizePool = c.prizePool,
                            isExpanded = showPrizeStructure,
                            onToggle = { showPrizeStructure = !showPrizeStructure },
                            colors = colors
                        )

                        // Add spacer for sticky CTA on mobile
                        if (isMobileView && c.canEnter && userEntries.isEmpty()) {
                            StickyBottomCTASpacer()
                        }
                    }
                }
            }
        }

        // Sticky CTA for mobile - always visible when user can enter and hasn't entered yet
        if (isMobileView && contest?.canEnter == true && userEntries.isEmpty()) {
            val showPaidGames = SharedData.showPaidGames.value
            StickyBottomCTA(
                text = "Enter Contest${if (contest?.entryFee == 0.0 && showPaidGames) " - FREE" else ""}",
                onClick = { NavigationState.navigate(Route.Lineup(contestId)) },
                subtitle = "Build your lineup now!"
            )
        }
    }
}

@Composable
private fun LoadingState(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, colors.primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, colors: ThemeColors, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(colors.error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

/**
 * Redesigned header card with Prize Pool as the hero element.
 */
@Composable
private fun ContestHeaderCard(contest: ContestEntity, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Badges row
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            ContestTypeBadge(contest.contestType)
            if (contest.isFeatured) FeaturedBadge()
            if (contest.isGuaranteed) GuaranteedBadge()
        }

        // HERO: Prize Pool
        Div({
            style {
                textAlign("center")
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    fontSize(36.px)
                    fontWeight(700)
                    color(colors.primary)
                }
            }) {
                Text("$${contest.prizePool.toInt()}")
            }
            Div({
                style {
                    fontSize(12.px)
                    color(colors.textSecondary)
                    property("text-transform", "uppercase")
                    letterSpacing(1.px)
                }
            }) {
                Text("PRIZE POOL")
            }
        }

        // Contest name
        H2({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(colors.textPrimary)
                textAlign("center")
                marginBottom(4.px)
            }
        }) {
            Text(ContestUtils.getDisplayName(contest.name))
        }

        // Tournament name
        if (contest.tournamentName.isNotBlank()) {
            P({
                style {
                    color(colors.textSecondary)
                    fontSize(14.px)
                    textAlign("center")
                    marginBottom(16.px)
                }
            }) {
                Text(contest.tournamentName)
            }
        }

        // Timing information
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(24.px)
                marginBottom(20.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            TimingBadge("Starts", contest.startTime, colors)
            TimingBadge("Locks", contest.rosterLockTime, colors)
        }

        // Key stats row (3 items - removed redundant Prize Pool)
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(3, 1fr)")
                gap(12.px)
            }
        }) {
            val showPaidGames = SharedData.showPaidGames.value
            StatCard("Entry Fee", if (contest.entryFee == 0.0 && showPaidGames) "FREE" else if (contest.entryFee == 0.0) "$0" else "$${contest.entryFee.toInt()}", colors)
            StatCard("Entries", "${contest.currentEntries} / ${contest.maxEntries}", colors)
            StatCard("Salary Cap", "$${contest.salaryCap}K", colors)
        }
    }
}

@Composable
private fun TimingBadge(label: String, timestamp: Long, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            fontSize(13.px)
            color(colors.textSecondary)
        }
    }) {
        Span { Text(if (label == "Starts") "📅" else "🔒") }
        Span({
            style { fontWeight(500) }
        }) {
            Text("$label: ${formatTimestamp(timestamp)}")
        }
    }
}

@Composable
private fun StatCard(label: String, value: String, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surfaceVariant)
            padding(12.px)
            borderRadius(8.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(colors.textSecondary)
                marginBottom(4.px)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(colors.textPrimary)
            }
        }) {
            Text(value)
        }
    }
}

/**
 * NEW: "How to Play" section for first-time user education.
 */
/**
 * Determines if a contest is for a Grand Slam tournament (best-of-5 for men's matches).
 */
private fun isGrandSlamContest(contestName: String): Boolean {
    val name = contestName.lowercase()
    return name.contains("australian open") ||
            name.contains("french open") ||
            name.contains("roland garros") ||
            name.contains("wimbledon") ||
            name.contains("us open")
}

@Composable
private fun HowToPlaySection(
    salaryCap: Int,
    contestName: String,
    isExpanded: Boolean,
    onToggle: () -> Unit,
    colors: ThemeColors
) {
    val isBestOfFive = isGrandSlamContest(contestName)
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            marginBottom(24.px)
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header (clickable)
        Div({
            style {
                padding(16.px, 20.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                cursor("pointer")
                backgroundColor(if (isExpanded) colors.highlightBackground else colors.surface)
            }
            onClick { onToggle() }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style { fontSize(20.px) }
                }) { Text("🎾") }
                H3({
                    style {
                        fontSize(16.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                    }
                }) {
                    Text("HOW TO PLAY")
                }
            }
            Span({
                style {
                    fontSize(20.px)
                    color(colors.textSecondary)
                }
            }) {
                Text(if (isExpanded) "−" else "+")
            }
        }

        // Expandable content
        if (isExpanded) {
            Div({
                style {
                    padding(20.px)
                }
            }) {
                // 3-Step Visual Flow
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        marginBottom(20.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    StepCard("1", "📋", "BUILD", "Pick 5 players for 5 skills. Stay under salary cap.", colors)
                    StepCard("2", "📺", "WATCH", "Your picks earn points based on real match stats.", colors)
                    StepCard("3", "🏆", "WIN", "Outscore other entries to win cash prizes!", colors)
                }

                // The 5 Skill Categories
                Div({
                    style {
                        marginBottom(16.px)
                    }
                }) {
                    Div({
                        style {
                            fontSize(12.px)
                            fontWeight(600)
                            color(colors.textSecondary)
                            marginBottom(12.px)
                            property("text-transform", "uppercase")
                            letterSpacing(0.5.px)
                        }
                    }) {
                        Text("THE 5 SKILL CATEGORIES")
                    }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(auto-fit, minmax(100px, 1fr))")
                            gap(8.px)
                        }
                    }) {
                        ScoringCategoryCard(Category.SERVE, "Ace +3", "DF -1", colors)
                        ScoringCategoryCard(Category.FOREHAND, "Winner +2", "Error -1", colors)
                        ScoringCategoryCard(Category.BACKHAND, "Winner +2", "Error -1", colors)
                        ScoringCategoryCard(Category.VOLLEYS, "Winner +3", null, colors)
                        ScoringCategoryCard(Category.FIGHTING, "Break +4", "Save +2", colors)
                    }
                }

                // Info Cards
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    // Salary Cap Info Card
                    InfoCard(
                        icon = "💰",
                        title = "SALARY CAP",
                        colors = colors
                    ) {
                        Div({
                            style {
                                fontSize(13.px)
                                color(colors.textSecondary)
                                lineHeight("1.5")
                            }
                        }) {
                            Text("Each player costs 1-12 points per category based on skill. Stay under the ")
                            Span({
                                style {
                                    fontWeight(600)
                                    color(colors.primary)
                                }
                            }) {
                                Text("$${salaryCap}K")
                            }
                            Text(" salary cap!")
                        }
                    }

                    // Quick Finish Bonus Card
                    InfoCard(
                        icon = "⚡",
                        title = "QUICK FINISH BONUS",
                        colors = colors
                    ) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(8.px)
                            }
                        }) {
                            if (isBestOfFive) {
                                // Best-of-5 (Grand Slams)
                                BonusRow(
                                    label = "3 sets",
                                    bonuses = listOf("+20" to "pts"),
                                    colors = colors
                                )
                                BonusRow(
                                    label = "4 sets",
                                    bonuses = listOf("+10" to "pts"),
                                    colors = colors
                                )
                                BonusRow(
                                    label = "5 sets",
                                    bonuses = listOf("+0" to "pts"),
                                    colors = colors
                                )
                            } else {
                                // Best-of-3 (most tournaments)
                                BonusRow(
                                    label = "2 sets",
                                    bonuses = listOf("+10" to "pts"),
                                    colors = colors
                                )
                            }
                        }
                    }

                    // Show Court Matches Info Card
                    InfoCard(
                        icon = "📺",
                        title = "SHOW COURT MATCHES",
                        colors = colors
                    ) {
                        Div({
                            style {
                                fontSize(13.px)
                                color(colors.textSecondary)
                                lineHeight("1.5")
                            }
                        }) {
                            Text("This slate includes ")
                            Span({
                                style {
                                    fontWeight(600)
                                    color(colors.primary)
                                }
                            }) {
                                Text("show court matches")
                            }
                            Text(" with full play-by-play statistics. Shot-by-shot data powers your fantasy scoring!")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StepCard(
    stepNumber: String,
    icon: String,
    title: String,
    description: String,
    colors: ThemeColors
) {
    Div({
        style {
            flex(1)
            // Use min() to prevent overflow on mobile - falls back to 100% if 140px is too wide
            property("min-width", "min(100%, 140px)")
            backgroundColor(colors.surfaceVariant)
            borderRadius(8.px)
            padding(16.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                width(28.px)
                height(28.px)
                borderRadius(50.percent)
                backgroundColor(colors.primary)
                color(colors.textOnPrimary)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                fontSize(14.px)
                fontWeight(600)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
                marginBottom(8.px)
            }
        }) {
            Text(stepNumber)
        }
        Div({
            style {
                fontSize(24.px)
                marginBottom(4.px)
            }
        }) {
            Text(icon)
        }
        Div({
            style {
                fontSize(14.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(4.px)
            }
        }) {
            Text(title)
        }
        Div({
            style {
                fontSize(12.px)
                color(colors.textSecondary)
                lineHeight("1.4")
            }
        }) {
            Text(description)
        }
    }
}

@Composable
private fun InfoCard(
    icon: String,
    title: String,
    colors: ThemeColors,
    content: @Composable () -> Unit
) {
    Div({
        style {
            flex(1)
            // Use min() to stack cards on mobile - falls back to 100% on narrow screens
            property("min-width", "min(100%, 250px)")
            backgroundColor(colors.surfaceVariant)
            borderRadius(8.px)
            padding(16.px)
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(12.px)
            }
        }) {
            Span({
                style { fontSize(18.px) }
            }) { Text(icon) }
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(600)
                    color(colors.textSecondary)
                    property("text-transform", "uppercase")
                    letterSpacing(0.5.px)
                }
            }) { Text(title) }
        }
        // Content
        content()
    }
}

@Composable
private fun BonusRow(
    label: String,
    bonuses: List<Pair<String, String>>,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        // Label
        Span({
            style {
                fontSize(13.px)
                fontWeight(500)
                color(colors.textPrimary)
                minWidth(70.px)
            }
        }) { Text(label) }

        // Bonus chips
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(6.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            bonuses.forEach { (points, condition) ->
                BonusChip(points, condition, colors)
            }
        }
    }
}

@Composable
private fun BonusChip(
    points: String,
    condition: String,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            backgroundColor(colors.surface)
            borderRadius(4.px)
            padding(4.px, 8.px)
        }
    }) {
        Span({
            style {
                fontSize(13.px)
                fontWeight(600)
                color(if (points == "+0") colors.textSecondary else colors.primary)
            }
        }) { Text(points) }
        Span({
            style {
                fontSize(11.px)
                color(colors.textSecondary)
            }
        }) { Text(condition) }
    }
}

/**
 * Unified entry management section - combines entry display and CTA.
 */
@Composable
private fun EntryManagementSection(
    contest: ContestEntity,
    userEntries: List<EntryEntity>,
    entryLineups: Map<String, LineupEntity>,
    slatePlayersMap: Map<String, TennisPlayerEntity>,
    onEnter: () -> Unit,
    onEditEntry: (EntryEntity) -> Unit,
    colors: ThemeColors
) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        if (userEntries.isNotEmpty()) {
            // User has entries - show them with option to add more
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(16.px)
                }
            }) {
                H3({
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                    }
                }) {
                    Text("YOUR ENTRIES (${userEntries.size})")
                }

                // Add entry button if contest allows and is open
                if (contest.canEnter) {
                    Button(
                        text = "+ Add Entry",
                        onClick = onEnter,
                        variant = "secondary"
                    )
                }
            }

            // Entry cards with lineup preview
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                    marginBottom(16.px)
                }
            }) {
                userEntries.forEach { entry ->
                    val lineup = entryLineups[entry.id]
                    EntryCardWithLineup(
                        entry = entry,
                        lineup = lineup,
                        players = slatePlayersMap,
                        contestStatus = contest.status,
                        onEdit = { onEditEntry(entry) },
                        onWatchLive = {
                            NavigationState.navigate(Route.GameDay(contest.id, entry.id))
                        },
                        colors = colors
                    )
                }
            }
        } else {
            // No entries - show welcome CTA
            H3({
                style {
                    fontSize(16.px)
                    fontWeight(600)
                    color(colors.textPrimary)
                    marginBottom(16.px)
                    textAlign("center")
                }
            }) {
                Text("READY TO PLAY?")
            }
        }

        // Progress bar / status
        SmartProgressBar(contest, colors)

        // CTA for users without entries or when contest allows more
        if (userEntries.isEmpty() && contest.canEnter) {
            Div({
                style {
                    marginTop(16.px)
                }
            }) {
                // Primary CTA button
                Div({
                    style {
                        backgroundColor(colors.primary)
                        color(colors.textOnPrimary)
                        padding(16.px, 24.px)
                        borderRadius(12.px)
                        fontSize(18.px)
                        fontWeight(700)
                        textAlign("center")
                        cursor("pointer")
                        property("box-shadow", "0 4px 12px rgba(0, 166, 81, 0.3)")
                        property("transition", "transform 0.2s, box-shadow 0.2s")
                    }
                    onClick { onEnter() }
                }) {
                    val showPaidGames = SharedData.showPaidGames.value
                    Text("🎾 Enter Contest${if (contest.entryFee == 0.0 && showPaidGames) " - FREE" else ""}")
                }

                // Micro-copy
                Div({
                    style {
                        textAlign("center")
                        marginTop(8.px)
                        fontSize(13.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("Build your lineup now!")
                }

                // Benefits
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        gap(16.px)
                        marginTop(12.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    val showPaidGamesForBenefits = SharedData.showPaidGames.value
                    if (contest.entryFee == 0.0 && showPaidGamesForBenefits) {
                        BenefitChip("Free to enter", colors)
                    }
                    BenefitChip("5 minutes to build", colors)
                    BenefitChip("Win real prizes", colors)
                }
            }
        }

        // Status message for closed contests (but not if manually unlocked)
        if (contest.status != ContestStatus.UPCOMING && !contest.isManuallyUnlocked) {
            Div({
                style {
                    backgroundColor(colors.surfaceVariant)
                    padding(16.px)
                    borderRadius(8.px)
                    textAlign("center")
                    marginTop(16.px)
                }
            }) {
                Text(
                    when (contest.status) {
                        ContestStatus.LIVE -> "Contest is live - entries closed"
                        ContestStatus.COMPLETED -> "Contest has ended"
                        ContestStatus.CANCELLED -> "Contest was cancelled"
                        else -> "Entries closed"
                    }
                )
            }
        } else if (contest.isFull) {
            Div({
                style {
                    backgroundColor(colors.errorLight)
                    padding(16.px)
                    borderRadius(8.px)
                    textAlign("center")
                    color(colors.errorDark)
                    marginTop(16.px)
                }
            }) {
                Text("Contest is full")
            }
        }
    }
}

@Composable
private fun EntryCardWithLineup(
    entry: EntryEntity,
    lineup: LineupEntity?,
    players: Map<String, TennisPlayerEntity>,
    contestStatus: ContestStatus,
    onEdit: () -> Unit,
    onWatchLive: () -> Unit,
    colors: ThemeColors
) {
    val isLive = contestStatus == ContestStatus.LIVE

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            backgroundColor(if (isLive) Color("rgba(76, 175, 80, 0.1)") else colors.highlightBackground)
            padding(12.px, 16.px)
            borderRadius(8.px)
            if (isLive) {
                property("border", "1px solid rgba(76, 175, 80, 0.3)")
            }
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            // Entry number badge
            Div({
                style {
                    width(32.px)
                    height(32.px)
                    borderRadius(50.percent)
                    backgroundColor(if (isLive) com.fantasytenis.web.theme.Colors.Live else colors.primary)
                    color(colors.textOnPrimary)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    fontSize(14.px)
                    fontWeight(600)
                }
            }) {
                Text("${entry.entryNumber}")
            }

            Div {
                // Entry title with live indicator
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                        fontWeight(500)
                        fontSize(14.px)
                        color(colors.textPrimary)
                    }
                }) {
                    Text("Entry #${entry.entryNumber}")
                    if (isLive) {
                        Span({
                            classes("game-day-live-indicator")
                            style {
                                width(6.px)
                                height(6.px)
                                borderRadius(50.percent)
                                backgroundColor(com.fantasytenis.web.theme.Colors.Live)
                            }
                        })
                    }
                }

                // Lineup preview - 5 player initials
                lineup?.let { l ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(4.px)
                            marginTop(4.px)
                        }
                    }) {
                        listOf(
                            l.slots.serve to Category.SERVE,
                            l.slots.forehand to Category.FOREHAND,
                            l.slots.backhand to Category.BACKHAND,
                            l.slots.volleys to Category.VOLLEYS,
                            l.slots.fighting to Category.FIGHTING
                        ).forEach { (playerId, category) ->
                            PlayerMiniAvatar(
                                playerId = playerId,
                                category = category,
                                players = players,
                                colors = colors
                            )
                        }
                    }
                }

                // Rank and points
                entry.rank?.let { rank ->
                    Div({
                        style {
                            fontSize(12.px)
                            color(colors.textSecondary)
                            marginTop(4.px)
                        }
                    }) {
                        Text("Rank: #$rank • ${entry.totalPoints} pts")
                    }
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
            }
        }) {
            if (isLive) {
                Button(
                    text = "Watch Live",
                    onClick = onWatchLive,
                    variant = "primary"
                )
            } else if (contestStatus == ContestStatus.UPCOMING) {
                Button(
                    text = "Edit Lineup",
                    onClick = onEdit,
                    variant = "secondary"
                )
            } else {
                Button(
                    text = "View",
                    onClick = onWatchLive,
                    variant = "secondary"
                )
            }
        }
    }
}

@Composable
private fun PlayerMiniAvatar(
    playerId: String?,
    category: Category,
    players: Map<String, TennisPlayerEntity>,
    colors: ThemeColors
) {
    val player = playerId?.let { players[it] }
    val initials = player?.name?.split(" ")?.mapNotNull { it.firstOrNull()?.uppercaseChar() }?.take(2)?.joinToString("") ?: "?"
    val categoryColor = getCategoryColor(category, colors)

    Div({
        style {
            width(24.px)
            height(24.px)
            borderRadius(50.percent)
            backgroundColor(categoryColor)
            color(colors.textOnPrimary)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            fontSize(9.px)
            fontWeight(600)
        }
        attr("title", player?.name ?: category.displayName)
    }) {
        Text(initials)
    }
}

@Composable
private fun BenefitChip(text: String, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            fontSize(12.px)
            color(colors.success)
        }
    }) {
        Span { Text("✓") }
        Span { Text(text) }
    }
}

/**
 * Smart progress bar with contextual treatment based on fill rate.
 */
@Composable
private fun SmartProgressBar(contest: ContestEntity, colors: ThemeColors) {
    val fillPercent = if (contest.maxEntries > 0) {
        (contest.currentEntries.toDouble() / contest.maxEntries * 100).coerceIn(0.0, 100.0)
    } else 0.0

    when {
        fillPercent == 0.0 -> {
            // "Be the first to enter!"
            Div({
                style {
                    textAlign("center")
                    padding(8.px)
                    color(colors.textSecondary)
                    fontSize(13.px)
                }
            }) {
                Text("✨ ${contest.spotsRemaining} spots available • Be the first to enter!")
            }
        }
        fillPercent < 25.0 -> {
            // "Just getting started"
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Span { Text("${contest.currentEntries} entered • ${contest.spotsRemaining} spots left") }
                Span { Text("Just getting started!") }
            }
        }
        fillPercent >= 90.0 -> {
            // Urgent treatment
            Div({
                style {
                    marginBottom(8.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        marginBottom(4.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(13.px)
                            color(colors.error)
                            fontWeight(600)
                        }
                    }) {
                        Text("⚡ Almost full! Only ${contest.spotsRemaining} spots left")
                    }
                    Span({
                        style {
                            fontSize(13.px)
                            fontWeight(500)
                            color(colors.error)
                        }
                    }) {
                        Text("${fillPercent.toInt()}% filled")
                    }
                }
                ProgressBar(fillPercent, colors.error, colors)
            }
        }
        else -> {
            // Standard progress bar
            Div({
                style {
                    marginBottom(8.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        marginBottom(4.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("${contest.spotsRemaining} spots remaining")
                    }
                    Span({
                        style {
                            fontSize(13.px)
                            fontWeight(500)
                            color(colors.textPrimary)
                        }
                    }) {
                        Text("${fillPercent.toInt()}% filled")
                    }
                }
                ProgressBar(fillPercent, colors.progressNormal, colors)
            }
        }
    }
}

@Composable
private fun ProgressBar(percent: Double, fillColor: CSSColorValue, colors: ThemeColors) {
    Div({
        style {
            width(100.percent)
            height(8.px)
            backgroundColor(colors.progressBackground)
            borderRadius(4.px)
            overflow("hidden")
        }
    }) {
        Div({
            style {
                width(percent.percent)
                height(100.percent)
                backgroundColor(fillColor)
                property("transition", "width 0.3s")
            }
        })
    }
}

/**
 * Improved prize structure section with medal icons and percentages.
 */
@Composable
private fun PrizeStructureSection(
    prizeStructure: List<PrizeLevel>,
    totalPrizePool: Double,
    isExpanded: Boolean,
    onToggle: () -> Unit,
    colors: ThemeColors
) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            marginBottom(24.px)
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header with preview
        Div({
            style {
                padding(20.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                cursor("pointer")
                backgroundColor(if (isExpanded) colors.surfaceVariant else colors.surface)
            }
            onClick { onToggle() }
        }) {
            Div {
                H3({
                    style {
                        fontSize(16.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                    }
                }) {
                    Text("Prize Structure")
                }
                // Preview text when collapsed
                if (!isExpanded && prizeStructure.isNotEmpty()) {
                    Div({
                        style {
                            fontSize(12.px)
                            color(colors.success)
                            marginTop(2.px)
                        }
                    }) {
                        Text("Win up to $${prizeStructure.firstOrNull()?.prize?.toInt() ?: 0}")
                    }
                }
            }
            Span({
                style {
                    fontSize(20.px)
                    color(colors.textSecondary)
                }
            }) {
                Text(if (isExpanded) "−" else "+")
            }
        }

        // Expandable content
        if (isExpanded) {
            Div({
                style {
                    padding(0.px, 24.px, 24.px)
                }
            }) {
                if (prizeStructure.isEmpty()) {
                    P({
                        style {
                            color(colors.textSecondary)
                            fontSize(14.px)
                        }
                    }) {
                        Text("Prize structure will be finalized when contest fills")
                    }
                } else {
                    // Table header
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "auto 1fr 1fr")
                            padding(12.px)
                            backgroundColor(colors.surfaceVariant)
                            borderRadius(8.px)
                            marginBottom(8.px)
                            fontSize(12.px)
                            fontWeight(600)
                            color(colors.textSecondary)
                            property("text-transform", "uppercase")
                        }
                    }) {
                        Span { Text("") } // Medal column
                        Span { Text("Place") }
                        Span({ style { textAlign("right") } }) { Text("Prize") }
                    }

                    // Prize rows
                    prizeStructure.forEachIndexed { _, level ->
                        val percentage = if (totalPrizePool > 0) {
                            ((level.prize / totalPrizePool) * 100).toInt()
                        } else 0

                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "auto 1fr 1fr")
                                padding(12.px)
                                property("border-bottom", "1px solid ${colors.borderLight}")
                                alignItems(AlignItems.Center)
                            }
                        }) {
                            // Medal icon for top 3
                            Span({
                                style {
                                    fontSize(16.px)
                                    width(24.px)
                                }
                            }) {
                                Text(
                                    when (level.minRank) {
                                        1 -> "🥇"
                                        2 -> "🥈"
                                        3 -> "🥉"
                                        else -> ""
                                    }
                                )
                            }

                            Span({
                                style {
                                    fontSize(14.px)
                                    color(colors.textPrimary)
                                }
                            }) {
                                Text(
                                    if (level.minRank == level.maxRank) {
                                        "${level.minRank}${getOrdinalSuffix(level.minRank)}"
                                    } else {
                                        "${level.minRank}${getOrdinalSuffix(level.minRank)} - ${level.maxRank}${getOrdinalSuffix(level.maxRank)}"
                                    }
                                )
                            }

                            Div({
                                style {
                                    textAlign("right")
                                }
                            }) {
                                Span({
                                    style {
                                        fontSize(14.px)
                                        fontWeight(600)
                                        color(colors.primary)
                                    }
                                }) {
                                    Text("$${level.prize.toInt()}")
                                }
                                if (percentage > 0) {
                                    Span({
                                        style {
                                            fontSize(11.px)
                                            color(colors.textSecondary)
                                            marginLeft(4.px)
                                        }
                                    }) {
                                        Text("(${percentage}%)")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// Utility functions

private fun formatTimestamp(timestamp: Long): String {
    if (timestamp <= 0) return "TBD"
    val date = Date(timestamp.toDouble())
    val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val month = monthNames[date.getMonth()]
    val day = date.getDate()
    val hours = date.getHours()
    val minutes = date.getMinutes().toString().padStart(2, '0')
    val ampm = if (hours >= 12) "PM" else "AM"
    val hour12 = if (hours % 12 == 0) 12 else hours % 12
    return "$month $day, $hour12:$minutes $ampm"
}

private fun getOrdinalSuffix(n: Int): String {
    return when {
        n in 11..13 -> "th"
        n % 10 == 1 -> "st"
        n % 10 == 2 -> "nd"
        n % 10 == 3 -> "rd"
        else -> "th"
    }
}
